import { Modal, Card, Typography, Space, Descriptions, Tag, Spin, message, Button } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { responseService } from '../../services/response.service';
import { parseApiError } from '../../utils/error-handler';
import { AGE_RANGE_LABELS } from '../../utils/age-range';
import {
  DONT_KNOW_OPTION_LABEL,
  DONT_KNOW_OPTION_VALUE,
  NULL_OPTION_LABEL,
  NULL_OPTION_VALUE,
} from '../../utils/questionOptions';

const { Text } = Typography;

interface ResponseAuditModalProps {
  open: boolean;
  onClose: () => void;
  responseHash: string;
  surveyName: string;
  surveyPublicAuditHash?: string;
}

interface AuditData {
  survey: {
    id: number;
    name: string;
    description?: string;
  };
  responseHash: string;
  submittedAt: string;
  profileSnapshot: {
    gender?: string;
    age_group?: string;
    education?: string;
    economic_level?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  answers: Array<{
    questionId: number;
    question: {
      id: number;
      order: number;
      shortDescription: string;
      longDescription?: string;
      questionType: string;
      options?: any;
    };
    answerType: string;
    textAnswer?: string;
    numericAnswer?: number;
    booleanAnswer?: boolean;
    choiceAnswers?: any;
    dateAnswer?: string;
  }>;
}

const ResponseAuditModal = ({ open, onClose, responseHash, surveyPublicAuditHash }: ResponseAuditModalProps) => {
  const [loading, setLoading] = useState(false);
  const [auditData, setAuditData] = useState<AuditData | null>(null);

  useEffect(() => {
    if (open && responseHash) {
      loadAuditData();
    }
  }, [open, responseHash]);

  const loadAuditData = async () => {
    setLoading(true);
    try {
      const data = await responseService.getResponseAuditByHash(responseHash);
      setAuditData(data);
    } catch (error) {
      const apiError = parseApiError(error);
      message.error(apiError.message);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAnswerDisplay = (answer: AuditData['answers'][0]) => {
    const mapDefaultOptionValue = (value: string) => {
      if (value === NULL_OPTION_VALUE) return NULL_OPTION_LABEL;
      if (value === DONT_KNOW_OPTION_VALUE) return DONT_KNOW_OPTION_LABEL;
      return value;
    };

    if (answer.textAnswer) return answer.textAnswer;
    if (answer.numericAnswer !== null && answer.numericAnswer !== undefined) {
      return answer.numericAnswer.toString();
    }
    if (answer.booleanAnswer !== null && answer.booleanAnswer !== undefined) {
      return answer.booleanAnswer ? 'Sim' : 'Não';
    }
    if (answer.choiceAnswers) {
      if (Array.isArray(answer.choiceAnswers)) {
        return answer.choiceAnswers.map((value) => mapDefaultOptionValue(String(value))).join(', ');
      }
      if (typeof answer.choiceAnswers === 'string') {
        return mapDefaultOptionValue(answer.choiceAnswers);
      }
      return JSON.stringify(answer.choiceAnswers);
    }
    if (answer.dateAnswer) {
      return new Date(answer.dateAnswer).toLocaleDateString('pt-BR');
    }
    return 'N/A';
  };

  const translateProfileField = (key: string, value: string) => {
    const translations: Record<string, string> = {
      gender: 'Gênero',
      age_group: 'Faixa Etária',
      education: 'Escolaridade',
      economic_level: 'Nível Econômico',
      city: 'Cidade',
      state: 'Estado',
      country: 'País',
    };

    const genderTranslations: Record<string, string> = {
      MALE: 'Masculino',
      FEMALE: 'Feminino',
      OTHER: 'Outro',
    };

    const educationTranslations: Record<string, string> = {
      FUNDAMENTAL: 'Ensino Fundamental',
      MEDIO: 'Ensino Médio',
      SUPERIOR: 'Ensino Superior',
      POS_GRADUACAO: 'Pós-Graduação',
      MESTRADO: 'Mestrado',
      DOUTORADO: 'Doutorado',
    };

    const economicTranslations: Record<string, string> = {
      NO_INCOME: 'Sem renda',
      UP_TO_HALF_MW: 'Até 0,5 Salário Mínimo',
      ONE_TO_TWO_MW: '1 a 2 Salários Mínimos',
      TWO_TO_FIVE_MW: '2 a 5 Salários Mínimos',
      FIVE_TO_TEN_MW: '5 a 10 Salários Mínimos',
      TEN_TO_TWENTY_MW: '10 a 20 Salários Mínimos',
      ABOVE_TWENTY_MW: 'Acima de 20 Salários Mínimos',
    };

    let translatedValue = value;
    const ageGroupLabel = AGE_RANGE_LABELS[value as keyof typeof AGE_RANGE_LABELS];
    if (key === 'gender') translatedValue = genderTranslations[value] || value;
    if (key === 'age_group') translatedValue = ageGroupLabel || value;
    if (key === 'education') translatedValue = educationTranslations[value] || value;
    if (key === 'economic_level') translatedValue = economicTranslations[value] || value;

    return {
      label: translations[key] || key,
      value: translatedValue,
    };
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={[
        surveyPublicAuditHash ? (
          <Button
            key="full-audit-trail"
            type="primary"
            icon={<LinkOutlined />}
            href={`/public/surveys/${surveyPublicAuditHash}/audit-trail`}
            target="_blank"
          >
            Ver trilha completa
          </Button>
        ) : null,
      ]}
      width={800}
      title="Auditoria de Pesquisa - Sua participação"
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : auditData ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Bloco 1: Data/Hora e Hash */}
          <Card type="inner" title="Informações da Resposta">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Data e Hora">
                {formatDate(auditData.submittedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Hash de Comprovação">
                <Text code copyable style={{ fontSize: '12px' }}>
                  {auditData.responseHash}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Bloco 2: Respostas */}
          <Card type="inner" title="Suas Respostas">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {auditData.answers.map((answer, index) => (
                <Card key={answer.questionId} size="small" type="inner">
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text strong>
                      {index + 1}. {answer.question.shortDescription}
                    </Text>
                    <Tag color="blue">{getAnswerDisplay(answer)}</Tag>
                  </Space>
                </Card>
              ))}
            </Space>
          </Card>

          {/* Bloco 3: Perfil Demográfico */}
          <Card type="inner" title="Perfil Demográfico Usado">
            <Descriptions column={1} bordered size="small">
              {Object.entries(auditData.profileSnapshot || {}).map(([key, value]) => {
                const { label, value: displayValue } = translateProfileField(key, value as string);
                return (
                  <Descriptions.Item key={key} label={label}>
                    {displayValue}
                  </Descriptions.Item>
                );
              })}
            </Descriptions>
          </Card>
        </Space>
      ) : (
        <Text type="secondary">Nenhum dado disponível</Text>
      )}
    </Modal>
  );
};

export default ResponseAuditModal;
